import React from "react";
import db from "../../../../db/index.json";
import ParagraphTag from "../../../reusebale/Paragraph";

const MONTHS_PER_YEAR = 12;
const RENT_FREQUENCY_YEARLY = "Yearly";

function toStartOfDay(d) {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function toEndOfDay(d) {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/** Days between two dates (inclusive). */
function daysBetween(start, end) {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end - start) / msPerDay) + 1;
}

/**
 * Proration factor for yearly lease: lease start → end of that year (or lease end if sooner).
 * Day-based: daysInPeriod / daysInYear. Dates normalized to start/end of day so Dec 31 is full day.
 */
function getYearlyProrationFactor(rentStartDate, endDate) {
  if (!rentStartDate) return 1;
  const start = new Date(rentStartDate);
  const year = start.getFullYear();
  const startOfYear = new Date(year, 0, 1, 0, 0, 0, 0);
  const endOfYear = new Date(year, 11, 31, 23, 59, 59, 999);
  const periodStart = toStartOfDay(start) > startOfYear ? toStartOfDay(start) : startOfYear;
  const periodEnd = endDate && toEndOfDay(new Date(endDate)) < endOfYear ? toEndOfDay(new Date(endDate)) : endOfYear;
  if (periodStart >= periodEnd) return 0;
  const daysInPeriod = daysBetween(periodStart, periodEnd);
  const daysInYear = daysBetween(startOfYear, endOfYear);
  return Math.min(1, daysInPeriod / daysInYear);
}

const RentBreakDown = ({ leaseDetails }) => {
  const { lease } = db;
  const isYearly = (leaseDetails?.rentFrequency || "").trim() === RENT_FREQUENCY_YEARLY;
  const yearlyMultiplier = isYearly ? MONTHS_PER_YEAR : 1;
  const prorationFactor = isYearly
    ? getYearlyProrationFactor(leaseDetails?.rentStartDate, leaseDetails?.endDate)
    : 1;
  const multiplier = isYearly ? yearlyMultiplier * prorationFactor : 1;

  function calculateTotalAmount(list, unitRent = 0) {
    if (!Array.isArray(list)) {
      throw new Error("Input must be an array of objects");
    }

    const total = list.reduce((sum, item) => {
      const raw = typeof item.amount === "string" ? parseFloat(item.amount) : Number(item.amount);
      if (isNaN(raw)) {
        throw new Error(`Invalid amount value for item: ${JSON.stringify(item)}`);
      }
      return sum + raw;
    }, 0);

    return total + (unitRent || 0);
  }

  const unitRent = Number(leaseDetails?.unitInfo?.rent || 0) || 0;
  const displayUnitRent = unitRent * multiplier;
  const totalAmount = calculateTotalAmount(
    leaseDetails?.additionalCharges ? leaseDetails?.additionalCharges : [],
    leaseDetails?.unitInfo?.rent || 0
  );
  const displayTotal = totalAmount * multiplier;

  return (
    <div className="h-full">
      <div className="w-[95%] mx-auto">
        <ParagraphTag
          content="Rent Breakdown"
          classes="text-darkColor font-popinsSemiBold text-lg pt-4 text-center"
        />

        {/* Base Rent - label shows (Yearly) or (Monthly), amounts × 12 when yearly with proration */}
        <div className="flex pt-5 justify-between">
          <div className="w-[50%]">
            <ParagraphTag
              content="Base Rent"
              classes="text-darkColor font-popinsSemiBold text-xs"
            />
            <ParagraphTag
              content={`Unit rent (${leaseDetails?.rentFrequency || "Monthly"})`}
              classes="text-darkColor/40 font-popinsRegular text-xs line-clamp-1"
            />
          </div>
          <div className="w-[50%] flex justify-end items-center">
            <ParagraphTag
              content={`$ ${displayUnitRent.toFixed(2)}`}
              classes="text-darkColor font-popinsSemiBold text-sm"
            />
          </div>
        </div>

        {/* Additional Charges */}
        {
          leaseDetails?.additionalCharges?.map((i) => {
            const rawAmount = typeof i.amount === "string" ? parseFloat(i.amount) : Number(i.amount);
            const displayAmount = (isNaN(rawAmount) ? 0 : rawAmount) * multiplier;
            return (
              <div key={i.title || i.description} className="flex pt-5 justify-between">
                <div className="w-[50%]">
                  <ParagraphTag
                    content={i.title}
                    classes="text-darkColor font-popinsSemiBold text-xs"
                  />
                  <ParagraphTag
                    content={i.description}
                    classes="text-darkColor/40 font-popinsRegular text-xs line-clamp-1"
                  />
                </div>
                <div className="w-[50%] flex justify-end items-center">
                  <ParagraphTag
                    content={`$ ${displayAmount.toFixed(2)}`}
                    classes="text-darkColor font-popinsSemiBold text-sm"
                  />
                </div>
              </div>
            )
          })
        }

        <div className="h-[1px] bg-darkColor/10 w-full mt-28"></div>
        <div className="flex py-5 justify-between">
          <div className="w-[50%]">
            <ParagraphTag
              content="Total Amount"
              classes="text-darkColor font-popinsSemiBold text-sm"
            />
          </div>

          <div className="w-[50%] flex justify-end items-center">
            <ParagraphTag
              content={`$ ${displayTotal.toFixed(2)}`}
              classes="text-darkColor font-popinsSemiBold text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentBreakDown;
